import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Sparkles,
  Loader2,
  TrendingUp,
  Zap,
  ShieldCheck,
  PenLine,
  Search,
  X,
} from "lucide-react";
import api from "../lib/api";

const CATEGORY_META = {
  seo: { icon: Search, label: "SEO" },
  performance: { icon: Zap, label: "Performance" },
  content: { icon: PenLine, label: "Content" },
  security: { icon: ShieldCheck, label: "Security" },
  growth: { icon: TrendingUp, label: "Growth" },
};

const InsightsPanel = ({ appId }) => {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/apps/${appId}/insights`);
      setInsights(data);
    } catch (e) {
      toast.error("Could not load insights");
    } finally {
      setLoading(false);
    }
  }, [appId]);

  useEffect(() => {
    load();
  }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/apps/${appId}/insights/generate`);
      setInsights((prev) => [...data, ...prev]);
      toast.success(`The concierge offers ${data.length} new insights.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const dismiss = async (id) => {
    const prev = insights;
    setInsights((p) => p.filter((i) => i.id !== id));
    try {
      await api.delete(`/apps/${appId}/insights/${id}`);
    } catch {
      setInsights(prev);
    }
  };

  return (
    <div className="regal-card tracing-beam overflow-hidden" data-testid="insights-panel">
      <div className="px-6 py-4 border-b border-seam flex items-center justify-between bg-elevated/40 relative z-10">
        <div className="flex items-center gap-3">
          <Sparkles className="w-4 h-4 text-champagne" strokeWidth={1.5} />
          <div>
            <p className="label-eyebrow">AI Insights</p>
            <h3 className="font-heading text-2xl mt-1">Recommendations</h3>
          </div>
        </div>
        <button
          type="button"
          onClick={generate}
          disabled={generating}
          data-testid="generate-insights-btn"
          className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-md bg-champagne text-obsidian font-medium hover:bg-champagne-soft transition-all disabled:opacity-60"
        >
          {generating ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Sparkles className="w-3.5 h-3.5" strokeWidth={1.7} />
          )}
          Generate
        </button>
      </div>

      <div className="p-6 space-y-4 relative z-10 max-h-[520px] overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-5 h-5 text-champagne animate-spin" />
          </div>
        ) : insights.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-textmuted text-sm max-w-sm mx-auto">
              No insights yet. Fill the Knowledge Base, then ask the concierge to
              survey your project for opportunities.
            </p>
          </div>
        ) : (
          insights.map((i) => {
            const meta = CATEGORY_META[i.category] || CATEGORY_META.growth;
            const Icon = meta.icon;
            return (
              <div
                key={i.id}
                data-testid={`insight-${i.id}`}
                className="bg-elevated/70 border border-white/5 rounded-lg p-5 group relative"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-md border border-champagne/30 bg-champagne/10">
                      <Icon className="w-3.5 h-3.5 text-champagne" strokeWidth={1.5} />
                    </div>
                    <span className="text-[10px] tracking-wider uppercase text-champagne font-medium">
                      {meta.label}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => dismiss(i.id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-textmuted hover:text-[#E5A09E]"
                    title="Dismiss"
                    data-testid={`insight-dismiss-${i.id}`}
                  >
                    <X className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </button>
                </div>
                <h4 className="font-heading text-xl text-textmain mt-3 leading-snug">
                  {i.title}
                </h4>
                <p className="text-sm text-textmuted leading-relaxed mt-2">{i.body}</p>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default InsightsPanel;
